import { Observable } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { clearAuthState } from '../auth';
import { createDefaultAuthFailureHandler, type AuthFailureConfig } from './types';
import {
  clearTokens,
  getRefreshToken,
  hasExceededMaxAttempts,
  isCurrentlyRefreshing,
  performTokenRefresh,
  queuePendingRequest,
  resetRefreshState,
  RETRY_CONTEXT_KEY,
  updateOperationWithNewToken,
} from './tokenRefresh';

function isAuthenticationError(error: { extensions?: { code?: string } }): boolean {
  return error.extensions?.code === 'UNAUTHENTICATED';
}

function isPermissionError(error: { extensions?: { code?: string } }): boolean {
  return error.extensions?.code === 'INSUFFICIENT_PERMISSIONS' ||
         error.extensions?.code === 'FORBIDDEN';
}

function handleAuthFailure(config: AuthFailureConfig): void {
  clearTokens();
  clearAuthState();
  resetRefreshState();
  config.onAuthFailure();
}

export function createErrorLink(config?: Partial<AuthFailureConfig>) {
  const authFailureConfig: AuthFailureConfig = {
    onAuthFailure: config?.onAuthFailure ?? createDefaultAuthFailureHandler(),
  };

  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    const operationName = operation.operationName;
    const context = operation.getContext();

    if (networkError) {
      console.error('[Apollo] Network error:', networkError);
    }

    if (!graphQLErrors) {
      return undefined;
    }

    for (const err of graphQLErrors) {
      if (operationName === 'RefreshToken') {
        handleAuthFailure(authFailureConfig);
        return;
      }

      if (isPermissionError(err)) {
        toast.error(i18n.t('permissionDenied', { ns: 'common' }));
        continue;
      }

      if (!isAuthenticationError(err)) {
        continue;
      }

      if (context[RETRY_CONTEXT_KEY]) {
        handleAuthFailure(authFailureConfig);
        return;
      }

      if (hasExceededMaxAttempts()) {
        handleAuthFailure(authFailureConfig);
        return;
      }

      if (!getRefreshToken()) {
        handleAuthFailure(authFailureConfig);
        return;
      }

      if (isCurrentlyRefreshing()) {
        return new Observable(observer => {
          queuePendingRequest((newToken: string) => {
            updateOperationWithNewToken(operation, newToken);
            forward(operation).subscribe(observer);
          });
        });
      }

      return new Observable(observer => {
        const abortController = new AbortController();

        performTokenRefresh(abortController.signal)
          .then(newToken => {
            if (newToken) {
              updateOperationWithNewToken(operation, newToken);
              forward(operation).subscribe(observer);
            } else {
              handleAuthFailure(authFailureConfig);
            }
          })
          .catch(() => {
            handleAuthFailure(authFailureConfig);
          });

        return () => {
          abortController.abort();
        };
      });
    }

    return undefined;
  });
}

export const errorLink = createErrorLink();
